import { UserInfoFI } from "@/components/HolderForm/UserInfoFI.ts";
import { detectCardBrand } from "@/components/Payment/PaymentForm/detectCardBrand.ts";
import { PaymentFI } from "@/components/Payment/PaymentForm/enums/PaymentFI.ts";
import { PaymentFormValues } from "@/components/Payment/PaymentForm/interfaces/PaymentFormValues.ts";
import { paymentFormValidationSchema } from "@/components/Payment/PaymentForm/paymentFormValidationSchema.ts";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { PaymentDetailsInput, PaymentType } from "@/helpers/types/types.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { HolderFormData } from "@/pages/ReservationHolderInformation/HolderFormData.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { JSEncrypt } from "jsencrypt";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useShallow } from "zustand/react/shallow";

export const usePaymentForm = (holderFormData: HolderFormData) => {
  const { paymentEnabled } = useGetOrganization();
  const { setPaymentData, calculatedCartData, setCardInfo } = useEngineStore(
    useShallow((state) => ({
      setPaymentData: state.setPaymentData,
      calculatedCartData: state.calculatedCartData,
      setCardInfo: state.setCardInfo,
    })),
  );

  const isForcedInquiry = isInquiryPaymentType(calculatedCartData);
  const isCardGuarantee = calculatedCartData?.paymentConfig?.paymentType === PaymentType.CardGuarantee;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    watch,
    setValue,
    reset,
    getValues,
    trigger,
  } = useForm<PaymentFormValues>({
    resolver: yupResolver(paymentFormValidationSchema),
    mode: "onBlur",
    defaultValues: { [PaymentFI.SameAsBooker]: true },
  });

  const watchSameAsBooker = watch(PaymentFI.SameAsBooker);
  const holderFirstName = holderFormData.watch(UserInfoFI.FirstName);
  const holderLastName = holderFormData.watch(UserInfoFI.LastName);

  // adds the same values as booker to the payment form when checkbox checked
  useEffect(() => {
    if (watchSameAsBooker && isCardGuarantee) {
      setValue(PaymentFI.HolderFirstName, holderFirstName);
      setValue(PaymentFI.HolderLastName, holderLastName);
      setPaymentData({ holderFirstName, holderLastName });
      // revalidates form
      void trigger([PaymentFI.HolderFirstName, PaymentFI.HolderLastName]);
    }
  }, [
    watchSameAsBooker,
    isCardGuarantee,
    setValue,
    holderFirstName,
    holderLastName,
    setPaymentData,
    trigger,
  ]);

  // resets value of payment form when checkbox unchecked
  // TODO: Merge with upper useEffect
  //   - Figure out why holderForm changes when paymentFormFields are being set
  useEffect(() => {
    if (!watchSameAsBooker && isCardGuarantee) {
      setValue(PaymentFI.HolderFirstName, "");
      setValue(PaymentFI.HolderLastName, "");
      setPaymentData({ holderFirstName: undefined, holderLastName: undefined });
      // revalidates form
      void trigger([PaymentFI.HolderFirstName, PaymentFI.HolderLastName]);
    }
  }, [watchSameAsBooker, isCardGuarantee, setValue, setPaymentData, trigger]);

  const resetCreditCardFields = () => {
    setValue(PaymentFI.HolderFirstName, "");
    setValue(PaymentFI.HolderLastName, "");
    setPaymentData(undefined);
    setCardInfo(undefined, undefined, undefined);
  };

  const onSubmit = (data: PaymentFormValues) => {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(import.meta.env.VITE_ENCRPYT_PUB_KEY);

    const encryptionData: Pick<PaymentFormValues, "cardNumber" & "expiryDate"> = {
      // remove spaces from card number
      [PaymentFI.CardNumber]: data.cardNumber.split(" ").join(""),
      // by default is in "mm/yy" format, but must be in "mmyy" format
      [PaymentFI.ExpiryDate]: data.expiryDate.split("/").join(""),
    };
    const encryptedCardDetails = encrypt.encrypt(JSON.stringify(encryptionData));

    if (encryptedCardDetails) {
      // When sameAsBooker is true, use booker's names (form fields may be empty after reset)
      const firstName = data.sameAsBooker ? holderFirstName ?? "" : data.holderFirstName ?? "";
      const lastName = data.sameAsBooker ? holderLastName ?? "" : data.holderLastName ?? "";

      const paymentData: PaymentDetailsInput = {
        holderFirstName: firstName,
        holderLastName: lastName,
        encryptedCardDetails,
      };

      setPaymentData(paymentData);
      setCardInfo(
        detectCardBrand(data[PaymentFI.CardNumber]),
        data[PaymentFI.CardNumber].slice(-4),
        data[PaymentFI.ExpiryDate],
      );
    }
  };

  return {
    paymentFormData: {
      control,
      handleSubmitPaymentForm: handleSubmit,
      errors,
      isValid,
      onSubmitPaymentForm: onSubmit,
      register,
      watch,
      setValue,
      reset,
      formEnabled: Boolean(!isForcedInquiry && paymentEnabled && isCardGuarantee),
      getValues,
      resetCreditCardFields,
    },
  };
};
